<template>
  <div class="min-h-screen bg-gray-50">
    <!-- Header -->
    <header class="bg-white shadow-sm">
      <div class="container mx-auto px-6 py-4">
        <div class="flex items-center justify-between">
          <div class="flex items-center space-x-2">
            <i class="fas fa-check-circle text-blue-600 text-xl"></i>
            <h1 class="text-xl font-semibold text-gray-800">券核销管理</h1>
          </div>
          <div class="flex items-center space-x-4">
            <div class="relative">
              <input
                type="text"
                v-model="searchKeyword"
                placeholder="搜索券码、订单号、用户..."
                class="border border-gray-300 rounded-lg py-2 px-4 pl-10 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                @keyup.enter="handleSearch"
              />
              <i class="fas fa-search absolute left-3 top-2.5 text-gray-400"></i>
            </div>
            <button 
              @click="handleExport"
              class="bg-blue-600 text-white px-4 py-2 rounded-lg !rounded-button whitespace-nowrap text-sm hover:bg-blue-700 transition-colors"
              :disabled="exportLoading"
            >
              <i class="fas fa-download mr-1"></i> 
              {{ exportLoading ? '导出中...' : '导出数据' }}
            </button>
          </div>
        </div>
      </div>
    </header>

    <!-- Main Content -->
    <main class="container mx-auto px-6 py-8">
      <!-- Summary Cards -->
      <div class="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <div class="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
          <div class="flex items-center">
            <div class="p-3 rounded-lg bg-blue-100">
              <i class="fas fa-dollar-sign text-blue-600"></i>
            </div>
            <div class="ml-4">
              <p class="text-sm text-gray-500">总核销金额</p>
              <p class="text-2xl font-bold text-gray-800">¥ {{ formatCurrency(statistics.total_amount) }}</p>
            </div>
          </div>
        </div>
        <div class="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
          <div class="flex items-center">
            <div class="p-3 rounded-lg bg-green-100">
              <i class="fas fa-ticket-alt text-green-600"></i>
            </div>
            <div class="ml-4">
              <p class="text-sm text-gray-500">总核销券数</p>
              <p class="text-2xl font-bold text-gray-800">{{ statistics.total_vouchers }} 张</p>
            </div>
          </div>
        </div>
        <div class="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
          <div class="flex items-center">
            <div class="p-3 rounded-lg bg-yellow-100">
              <i class="fas fa-calendar-day text-yellow-600"></i>
            </div>
            <div class="ml-4">
              <p class="text-sm text-gray-500">今日核销金额</p>
              <p class="text-2xl font-bold text-gray-800">¥ {{ formatCurrency(statistics.today_amount) }}</p>
            </div>
          </div>
        </div>
        <div class="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
          <div class="flex items-center">
            <div class="p-3 rounded-lg bg-purple-100">
              <i class="fas fa-store text-purple-600"></i>
            </div>
            <div class="ml-4">
              <p class="text-sm text-gray-500">合作商家数</p>
              <p class="text-2xl font-bold text-gray-800">{{ statistics.merchant_count }} 家</p>
            </div>
          </div>
        </div>
      </div>

      <!-- Date Filter -->
      <div class="bg-white rounded-xl shadow-sm p-4 mb-6 border border-gray-100">
        <div class="flex flex-wrap items-center gap-4">
          <div class="flex items-center space-x-2">
            <label class="text-sm text-gray-600 whitespace-nowrap">核销时间：</label>
            <el-date-picker
              v-model="dateRange"
              type="daterange"
              range-separator="至"
              start-placeholder="开始日期"
              end-placeholder="结束日期"
              format="yyyy-MM-dd"
              value-format="yyyy-MM-dd"
              size="small"
              @change="handleDateChange"
              class="w-64"
            />
          </div>
          
          <div class="flex items-center space-x-2">
            <label class="text-sm text-gray-600 whitespace-nowrap">核销状态：</label>
            <el-select
              v-model="is_veri"
              placeholder="请选择状态"
              size="small"
              class="w-32"
              @change="handleStatusChange"
            >
              <el-option label="全部" value="" />
              <el-option label="已核销" :value="1" />
              <el-option label="未核销" :value="0" />
            </el-select>
          </div>

          <!-- <div class="flex items-center space-x-2">
            <label class="text-sm text-gray-600 whitespace-nowrap">订单状态：</label>
            <el-select
              v-model="orderStatus"
              placeholder="订单状态"
              size="small"
              class="w-32"
              @change="handleOrderStatusChange"
            >
              <el-option label="全部" value="" />
              <el-option label="待付款" :value="10" />
              <el-option label="待发货" :value="20" />
              <el-option label="待收货" :value="30" />
              <el-option label="已完成" :value="40" />
              <el-option label="已取消" :value="50" />
            </el-select>
          </div> -->
          
          <button
            @click="resetFilters"
            class="px-3 py-1.5 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            重置筛选
          </button>
        </div>
      </div>

      <!-- Merchant Filter -->
      <div class="bg-white rounded-xl shadow-sm p-4 mb-6 border border-gray-100 overflow-x-auto">
        <div class="flex space-x-3">
          <button 
            :class="['px-4 py-2 rounded-lg whitespace-nowrap text-sm transition-colors', 
                     selectedMerchant === 'all' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200']"
            @click="selectMerchant('all')"
          >
            全部商家
          </button>
          <button 
            v-for="(merchant, index) in merchants" 
            :key="index"
            :class="['px-4 py-2 rounded-lg whitespace-nowrap text-sm transition-colors', 
                     selectedMerchant === merchant.supply_id ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200']"
            @click="selectMerchant(merchant.supply_id)"
          >
            {{ merchant.name }}
          </button>
        </div>
      </div>

      <!-- Voucher List -->
      <div class="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div class="px-6 py-4 border-b border-gray-100 flex justify-between items-center">
          <div>
            <h2 class="text-lg font-semibold text-gray-800">核销记录</h2>
            <p class="text-sm text-gray-500 mt-1">共 {{ totalVouchers }} 条核销记录</p>
          </div>
          <div class="flex items-center space-x-2">
            <span class="text-sm text-gray-600">每页显示：</span>
            <el-select
              v-model="itemsPerPage"
              size="mini"
              class="w-20"
              @change="handlePageSizeChange"
            >
              <el-option :value="10" label="10条" />
              <el-option :value="20" label="20条" />
              <el-option :value="50" label="50条" />
              <el-option :value="100" label="100条" />
            </el-select>
          </div>
        </div>
        
        <div v-if="loading" class="p-8 text-center">
          <i class="fas fa-spinner fa-spin text-blue-600 text-xl"></i>
          <p class="mt-2 text-gray-500">加载中...</p>
        </div>
        
        <div v-else-if="vouchers.length === 0" class="p-8 text-center">
          <i class="fas fa-inbox text-gray-300 text-3xl"></i>
          <p class="mt-2 text-gray-500">暂无核销记录</p>
        </div>
        
        <div v-else class="overflow-x-auto">
          <table class="min-w-full divide-y divide-gray-200">
            <thead class="bg-gray-50">
              <tr>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">券编号</th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">商家</th>
                <!-- <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">券面额</th> -->
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">核销金额</th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">核销时间</th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">状态</th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">操作</th>
              </tr>
            </thead>
            <tbody class="bg-white divide-y divide-gray-200">
              <tr v-for="(voucher, index) in vouchers" :key="index" 
                  :class="voucher.is_veri === 1 ? 'bg-green-50' : ''">
                <td class="px-6 py-4 whitespace-nowrap">
                  <div class="flex items-center">
                    <div :class="['flex-shrink-0 h-10 w-10 rounded-lg flex items-center justify-center',
                      voucher.is_veri === 1 ? 'bg-green-100' : 'bg-blue-100']">
                      <i :class="[voucher.is_veri === 1 ? 'fas fa-check-circle text-green-600' : 'fas fa-ticket-alt text-blue-600']"></i>
                    </div>
                    <div class="ml-4">
                      <div class="text-sm font-medium text-gray-900">{{ voucher.code }}</div>
                      <div class="text-sm text-gray-500">{{ voucher.voucher_type }}</div>
                      <div class="text-xs text-gray-400 mt-0.5">{{ voucher.order_no }}</div>
                    </div>
                  </div>
                </td>
                <td class="px-6 py-4 whitespace-nowrap">
                  <div class="text-sm text-gray-900">{{ voucher.supply_name }}</div>
                  <div class="text-sm text-gray-500">{{ voucher.location }}</div>
                  <div class="text-xs text-gray-400 mt-0.5">{{ voucher.supply_phone }}</div>
                </td>
                <!-- <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  ¥ {{ formatCurrency(voucher.face_value) }}
                  <div v-if="voucher.coupon_money > 0" class="text-xs text-green-600">
                    优惠: ¥{{ formatCurrency(voucher.coupon_money) }}
                  </div>
                </td> -->
                <td class="px-6 py-4 whitespace-nowrap">
                  <div class="text-sm font-semibold text-green-600">¥ {{ formatCurrency(voucher.coupon_money) }}</div>
                  <!-- <div v-if="voucher.points_money > 0" class="text-xs text-yellow-600">
                    积分抵扣: ¥{{ formatCurrency(voucher.points_money) }}
                  </div> -->
                </td>
                <td class="px-6 py-4 whitespace-nowrap">
                  <div v-if="voucher.veri_time_formatted" class="text-sm text-gray-900">
                    {{ formatTime(voucher.veri_time_formatted) }}
                  </div>
                  <div v-else class="text-sm text-gray-400">-</div>
                  <div class="text-xs text-gray-500 mt-0.5">
                    创建: {{ formatTime(voucher.create_time_formatted) }}
                  </div>
                </td>
                <td class="px-6 py-4 whitespace-nowrap">
                  <span 
                    :class="['px-2 inline-flex text-xs leading-5 font-semibold rounded-full',
                      voucher.is_veri === 1 ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                    ]"
                  >
                    {{ voucher.is_veri_text }}
                  </span>
                  <div class="text-xs text-gray-500 mt-1">
                    {{ voucher.order_status_text }}
                  </div>
                  <div v-if="voucher.pay_status_text" class="text-xs text-gray-500 mt-0.5">
                    {{ voucher.pay_status_text }}
                  </div>
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm">
                  <button 
                    v-if="voucher.is_veri !== 1"
                    @click="handleVerify(voucher)"
                    class="bg-blue-600 text-white px-3 py-1.5 rounded-lg !rounded-button whitespace-nowrap text-sm hover:bg-blue-700 transition-colors flex items-center"
                    :disabled="verifying"
                  >
                    <i class="fas fa-check mr-1"></i> 
                    {{ verifying ? '核销中...' : '核销' }}
                  </button>
                  <div v-else class="text-gray-500 text-sm">
                    <div class="flex items-center">
                      <i class="fas fa-user-check mr-1 text-sm text-green-600"></i>
                      <span class="text-green-600">已核销</span>
                    </div>
                    <div class="text-xs text-gray-400 mt-0.5">
                      {{ voucher.veri_user_id ? '核销员: ' + voucher.veri_user_id : '' }}
                    </div>
                    <div v-if="voucher.remark" class="text-xs text-gray-500 mt-1 max-w-xs truncate" :title="voucher.remark">
                      备注: {{ voucher.remark }}
                    </div>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        
        <!-- Pagination -->
        <div v-if="!loading && vouchers.length > 0" class="px-6 py-4 border-t border-gray-100 flex items-center justify-between">
          <div class="text-sm text-gray-700">
            显示第 {{ (currentPage - 1) * itemsPerPage + 1 }} 至 {{ Math.min(currentPage * itemsPerPage, totalVouchers) }} 条，
            共 {{ totalVouchers }} 条
          </div>
          <el-pagination
            :current-page="currentPage"
            :page-size="itemsPerPage"
            :total="totalVouchers"
            layout="prev, pager, next, jumper"
            @current-change="handleCurrentChange"
            class="!inline-block"
          />
        </div>
      </div>

      <!-- Merchant Summary -->
     <div class="mt-8 bg-white rounded-xl shadow-sm p-6 border border-gray-100">
  <h2 class="text-lg font-semibold text-gray-800 mb-4">商家核销汇总</h2>
  <div v-if="loadingMerchant" class="text-center py-4">
    <i class="fas fa-spinner fa-spin text-blue-600"></i>
    <p class="mt-2 text-gray-500">加载中...</p>
  </div>
  <div v-else-if="merchantSummary.length === 0" class="text-center py-8 text-gray-500">
    暂无商家汇总数据
  </div>
  <div v-else class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
    <div v-for="(merchant, index) in merchantSummary" :key="index" class="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
      <div class="flex items-center justify-between">
        <h3 class="font-medium text-gray-800">{{ merchant.merchant_name }}</h3>
        <span class="text-sm text-gray-500">{{ merchant.location }}</span>
      </div>
      <div class="mt-4 space-y-2">
        <!-- <div class="flex justify-between items-center">
          <span class="text-sm text-gray-600">券面总额</span>
          <span class="text-sm font-semibold text-gray-900">¥ {{ formatCurrency(merchant.original_amount || merchant.total_face_value) }}</span>
        </div> -->
        <div class="flex justify-between items-center">
          <span class="text-sm text-gray-600">核销金额</span>
          <span class="text-sm font-semibold text-gray-900">¥ {{ formatCurrency(merchant.total_amount) }}</span>
        </div>
        <!-- <div class="flex justify-between items-center">
          <span class="text-sm text-gray-600">抵扣金额</span>
          <span class="text-sm font-semibold text-red-600">¥ {{ formatCurrency(merchant.total_deduction_amount) }}</span>
        </div> -->
        <div class="flex justify-between items-center">
          <span class="text-sm text-gray-600">核销券数</span>
          <span class="text-sm font-semibold text-gray-900">{{ merchant.total_vouchers }} 张</span>
        </div>
        <div class="flex justify-between items-center">
          <span class="text-sm text-gray-600">平均券额</span>
          <span class="text-sm font-semibold text-gray-900">¥ {{ formatCurrency(merchant.avg_amount) }}</span>
        </div>
        <!-- 优惠券和积分抵扣详情 -->
        <!-- <div v-if="merchant.total_discount_amount > 0 || merchant.total_points_amount > 0" class="pt-2 border-t border-gray-100">
          <div class="text-xs text-gray-500 space-y-1">
            <div v-if="merchant.total_discount_amount > 0" class="flex justify-between">
              <span>优惠券抵扣:</span>
              <span class="text-red-500">¥ {{ formatCurrency(merchant.total_discount_amount) }}</span>
            </div>
            <div v-if="merchant.total_points_amount > 0" class="flex justify-between">
              <span>积分抵扣:</span>
              <span class="text-red-500">¥ {{ formatCurrency(merchant.total_points_amount) }}</span>
            </div>
          </div>
        </div> -->
        <div class="pt-2 border-t border-gray-100">
          <div class="flex justify-between text-xs text-gray-400">
            <span>首单: {{ formatTime(merchant.first_verify_time_formatted) }}</span>
            <span>末单: {{ formatTime(merchant.last_verify_time_formatted) }}</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</div>
    </main>

    <!-- Verify Dialog -->
    <el-dialog
      :visible.sync="verifyDialogVisible"
      :title="selectedVoucher ? `核销确认 - ${selectedVoucher.code}` : '核销确认'"
      width="500px"
      :close-on-click-modal="false"
    >
      <div class="space-y-4">
        <div v-if="selectedVoucher" class="bg-gray-50 p-4 rounded-lg">
          <div class="flex items-center space-x-3">
            <div class="flex-shrink-0 h-10 w-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <i class="fas fa-ticket-alt text-blue-600"></i>
            </div>
            <div>
              <div class="font-medium text-gray-900">{{ selectedVoucher.code }}</div>
              <div class="text-sm text-gray-500">{{ selectedVoucher.voucher_type }}</div>
            </div>
          </div>
          <div class="mt-3 grid grid-cols-2 gap-3 text-sm">
            <div>
              <span class="text-gray-500">商家:</span>
              <span class="ml-2 font-semibold">{{ selectedVoucher.supply_name }}</span>
            </div>
            <div>
              <span class="text-gray-500">订单号:</span>
              <span class="ml-2">{{ selectedVoucher.order_no }}</span>
            </div>
            <div>
              <span class="text-gray-500">券面额:</span>
              <span class="ml-2 font-semibold">¥ {{ formatCurrency(selectedVoucher.face_value) }}</span>
            </div>
            <div>
              <span class="text-gray-500">用户:</span>
              <span class="ml-2">{{ selectedVoucher.user_name || '未知用户' }}</span>
            </div>
          </div>
        </div>
        
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-2">核销备注 <span class="text-gray-400">(选填)</span></label>
          <el-input
            v-model="verifyRemark"
            type="textarea"
            :rows="3"
            placeholder="请输入核销备注信息"
            maxlength="200"
            show-word-limit
          />
        </div>

        <div v-if="selectedVoucher && selectedVoucher.order_status === 10" class="bg-yellow-50 border border-yellow-200 rounded p-3">
          <div class="flex items-center">
            <i class="fas fa-exclamation-triangle text-yellow-500 mr-2"></i>
            <span class="text-sm text-yellow-700">该订单状态为待付款，确认继续核销吗？</span>
          </div>
        </div>
      </div>
      
      <template #footer>
        <span class="dialog-footer">
          <el-button @click="verifyDialogVisible = false">取消</el-button>
          <el-button   type="primary"  v-auth="'/statistics/voucher/verify'"  @click="confirmVerify" :loading="verifying">
            确认核销
          </el-button>
        </span>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import VoucherApi from '@/api/statistics.js';

export default {
  name: 'VoucherManagement',
  
  data() {
    return {
      // 数据状态
      statistics: {
        total_amount: 0,
        total_vouchers: 0,
        today_amount: 0,
        merchant_count: 0
      },
      
      merchants: [],
      vouchers: [],
      merchantSummary: [],
      
      // 筛选条件
      selectedMerchant: 'all',
      is_veri: '',
      searchKeyword: '',
      dateRange: [],
      orderStatus: '',
      
      // 分页
      currentPage: 1,
      itemsPerPage: 10,
      totalVouchers: 0,
      
      // 加载状态
      loading: false,
      loadingMerchant: false,
      verifying: false,
      exportLoading: false,
      
      // 核销对话框
      verifyDialogVisible: false,
      selectedVoucher: null,
      verifyRemark: ''
    }
  },
  
  computed: {
    totalPages() {
      return Math.ceil(this.totalVouchers / this.itemsPerPage)
    },
    
    // 构建导出参数
    exportParams() {
      const params = {
        merchant: this.selectedMerchant === 'all' ? 'all' : '',
        is_veri: this.is_veri,
        order_status: this.orderStatus,
        page: 1,
        list_rows: 10000 // 导出时获取所有数据
      }
      
      if (this.selectedMerchant !== 'all') {
        const merchant = this.merchants.find(m => m.supply_id == this.selectedMerchant)
        if (merchant) {
          params.merchant = merchant.name
        }
      }
      
      if (this.searchKeyword) {
        if (/^VC\d+$/.test(this.searchKeyword)) {
          params.code = this.searchKeyword
        } else if (/^\d+$/.test(this.searchKeyword)) {
          params.order_no = this.searchKeyword
        } else {
          params.user_name = this.searchKeyword
        }
      }
      
      if (this.dateRange && this.dateRange.length === 2) {
        params.start_time = this.dateRange[0]
        params.end_time = this.dateRange[1]
      }
      
      return params
    }
  },
  
  watch: {
    currentPage() {
      this.getVoucherList()
    }
  },
  
  mounted() {
    this.initData()
  },
  
  methods: {
    // 初始化数据
    async initData() {
      try {
        await Promise.all([
          this.getStatistics(),
          this.getMerchantList(),
          this.getMerchantSummary(),
          this.getVoucherList()
        ])
      } catch (error) {
        console.error('初始化数据失败:', error)
        this.$message.error('初始化数据失败')
      }
    },
    
    // 获取统计数据
    async getStatistics() {
      try {
        const params = {}
        if (this.dateRange && this.dateRange.length === 2) {
          params.start_time = this.dateRange[0]
          params.end_time = this.dateRange[1]
        }
        const response = await VoucherApi.getVoucherStatistics(params)
        this.statistics = response.data
      } catch (error) {
        console.error('获取统计数据失败:', error)
        this.$message.error('获取统计数据失败')
      }
    },
    
    // 获取商家列表
    async getMerchantList() {
      try {
        const response = await VoucherApi.getMerchantList({
          page: 1,
          list_rows: 50
        })
        this.merchants = response.data.data || []
      } catch (error) {
        console.error('获取商家列表失败:', error)
        this.$message.error('获取商家列表失败')
      }
    },
    
    // 获取商家汇总
    async getMerchantSummary() {
      this.loadingMerchant = true
      try {
        const params = {}
        if (this.dateRange && this.dateRange.length === 2) {
          params.start_time = this.dateRange[0]
          params.end_time = this.dateRange[1]
        }
        const response = await VoucherApi.getMerchantSummary(params)
        this.merchantSummary = response.data || []
      } catch (error) {
        console.error('获取商家汇总失败:', error)
        this.$message.error('获取商家汇总失败')
      } finally {
        this.loadingMerchant = false
      }
    },
    
    // 获取核销列表
    async getVoucherList() {
      this.loading = true
      try {
        const params = {
          page: this.currentPage,
          list_rows: this.itemsPerPage,
          merchant: this.selectedMerchant === 'all' ? 'all' : '',
          is_veri: this.is_veri,
          order_status: this.orderStatus
        }
        
        if (this.selectedMerchant !== 'all') {
          const merchant = this.merchants.find(m => m.supply_id == this.selectedMerchant)
          if (merchant) {
            params.merchant = merchant.name
          }
        }
        
        if (this.searchKeyword) {
          if (/^VC\d+$/.test(this.searchKeyword)) {
            params.code = this.searchKeyword
          } else if (/^\d+$/.test(this.searchKeyword)) {
            params.order_no = this.searchKeyword
          } else {
            params.user_name = this.searchKeyword
          }
        }
        
        if (this.dateRange && this.dateRange.length === 2) {
          params.start_time = this.dateRange[0]
          params.end_time = this.dateRange[1]
        }
        
        const response = await VoucherApi.getVoucherList(params)
        this.vouchers = response.data.data || []
        this.totalVouchers = response.data.total || 0
      } catch (error) {
        console.error('获取核销列表失败:', error)
        this.$message.error('获取核销列表失败')
        this.vouchers = []
        this.totalVouchers = 0
      } finally {
        this.loading = false
      }
    },
    
    // 选择商家
    selectMerchant(merchantId) {
      this.selectedMerchant = merchantId
      this.currentPage = 1
      this.getVoucherList()
      this.getStatistics()
    },
    
    // 搜索
    handleSearch() {
      this.currentPage = 1
      this.getVoucherList()
    },
    
    // 日期筛选
    handleDateChange() {
      this.currentPage = 1
      this.getVoucherList()
      this.getStatistics()
      this.getMerchantSummary()
    },
    
    // 状态筛选
    handleStatusChange() {
      this.currentPage = 1
      this.getVoucherList()
    },

    // 订单状态筛选
    handleOrderStatusChange() {
      this.currentPage = 1
      this.getVoucherList()
    },
    
    // 重置筛选
    resetFilters() {
      this.selectedMerchant = 'all'
      this.is_veri = ''
      this.searchKeyword = ''
      this.dateRange = []
      this.orderStatus = ''
      this.currentPage = 1
      this.itemsPerPage = 10
      this.getVoucherList()
      this.getStatistics()
      this.getMerchantSummary()
    },
    
    // 分页
    prevPage() {
      if (this.currentPage > 1) {
        this.currentPage--
        this.getVoucherList()
      }
    },
    
    nextPage() {
      if (this.currentPage < this.totalPages) {
        this.currentPage++
        this.getVoucherList()
      }
    },

    // 页码改变
    handleCurrentChange(page) {
      this.currentPage = page
      this.getVoucherList()
    },

    // 每页条数改变
    handlePageSizeChange(size) {
      this.itemsPerPage = size
      this.currentPage = 1
      this.getVoucherList()
    },
    
    // 核销操作
    handleVerify(voucher) {
      if (voucher.is_veri === 1) {
        this.$message.warning('该券已核销')
        return
      }
      
      this.selectedVoucher = voucher
      this.verifyRemark = ''
      this.verifyDialogVisible = true
    },
    
    // 确认核销
    async confirmVerify() {
      if (!this.selectedVoucher) return
      
      this.verifying = true
      try {
        const params = {
          code: this.selectedVoucher.code,
          veri_user_id: this.$store.state.user.userId || 1, // 这里应该是当前登录用户的ID
          remark: this.verifyRemark,
          veri_supply_id: this.selectedVoucher.veri_supply_id || this.selectedMerchant !== 'all' ? this.selectedMerchant : 0
        }
        
        const response = await VoucherApi.verifyVoucher(params)
        
        if (response.code === 1) {
          this.$message.success('核销成功')
          this.verifyDialogVisible = false
          // 刷新数据
          this.getVoucherList()
          this.getStatistics()
          this.getMerchantSummary()
        } else {
          this.$message.error(response.msg || '核销失败')
        }
      } catch (error) {
        console.error('核销失败:', error)
        this.$message.error(error.message || '核销失败')
      } finally {
        this.verifying = false
      }
    },
    
    // 导出数据
    async handleExport() {
      this.exportLoading = true
      try {
        // 获取所有数据用于导出
        const response = await VoucherApi.getVoucherList(this.exportParams)
        const exportData = response.data.data || []
        
        if (exportData.length === 0) {
          this.$message.warning('没有数据可以导出')
          return
        }
        
        // 格式化导出数据
        const formattedData = this.formatExportData(exportData)
        
        // 导出Excel文件
        this.exportToExcel(formattedData)
        
        this.$message.success(`导出成功，共${formattedData.length}条记录`)
      } catch (error) {
        console.error('导出失败:', error)
        this.$message.error('导出失败')
      } finally {
        this.exportLoading = false
      }
    },

    // 格式化导出数据
    formatExportData(data) {
      return data.map(item => {
        return {
          '券编号': item.code || '',
          '券类型': item.voucher_type || '',
          '商家名称': item.supply_name || '',
          '商家地址': item.location || '',
          '商家电话': item.supply_phone || '',
          '券面额': `¥${this.formatCurrency(item.face_value)}`,
          '优惠金额': `¥${this.formatCurrency(item.coupon_money)}`,
          '积分抵扣': `¥${this.formatCurrency(item.points_money)}`,
          '核销金额': `¥${this.formatCurrency(item.used_amount)}`,
          '核销状态': item.is_veri_text || '',
          '核销时间': item.veri_time_formatted || '',
          '核销员ID': item.veri_user_id || '',
          '核销备注': item.remark || '',
          '订单号': item.order_no || '',
          '订单金额': `¥${this.formatCurrency(item.order_amount)}`,
          '订单状态': item.order_status_text || '',
          '支付状态': item.pay_status_text || '',
          '支付时间': item.pay_time_formatted || '',
          '用户昵称': item.user_name || '',
          '创建时间': item.create_time_formatted || '',
          '更新时间': item.update_time_formatted || ''
        }
      })
    },
    
    // 导出Excel
    exportToExcel(data) {
      try {
        if (window.XLSX && window.XLSX.utils) {
          const XLSX = window.XLSX
          // 创建工作表
          const ws = XLSX.utils.json_to_sheet(data)
          
          // 设置列宽
          const wscols = [
            { wch: 15 }, // 券编号
            { wch: 10 }, // 券类型
            { wch: 20 }, // 商家名称
            { wch: 25 }, // 商家地址
            { wch: 15 }, // 商家电话
            { wch: 12 }, // 券面额
            { wch: 12 }, // 优惠金额
            { wch: 12 }, // 积分抵扣
            { wch: 12 }, // 核销金额
            { wch: 10 }, // 核销状态
            { wch: 20 }, // 核销时间
            { wch: 12 }, // 核销员ID
            { wch: 20 }, // 核销备注
            { wch: 20 }, // 订单号
            { wch: 12 }, // 订单金额
            { wch: 10 }, // 订单状态
            { wch: 10 }, // 支付状态
            { wch: 20 }, // 支付时间
            { wch: 15 }, // 用户昵称
            { wch: 20 }, // 创建时间
            { wch: 20 }  // 更新时间
          ]
          ws['!cols'] = wscols
          
          // 创建工作簿
          const wb = XLSX.utils.book_new()
          XLSX.utils.book_append_sheet(wb, ws, '核销记录')
          
          // 生成文件名
          const dateStr = new Date().toISOString().slice(0, 10).replace(/-/g, '')
          const timeStr = new Date().toTimeString().slice(0, 8).replace(/:/g, '')
          const fileName = `核销记录_${dateStr}_${timeStr}.xlsx`
          
          // 保存文件
          XLSX.writeFile(wb, fileName)
        } else {
          // 降级方案：下载CSV文件
          this.exportToCSV(data)
        }
      } catch (error) {
        console.error('导出文件失败:', error)
        // 尝试CSV导出作为后备方案
        this.exportToCSV(data)
      }
    },

    // 导出CSV
    exportToCSV(data) {
      try {
        if (data.length === 0) {
          this.$message.warning('没有数据可以导出')
          return
        }
        
        // 获取表头
        const headers = Object.keys(data[0])
        // 构建CSV内容
        const csvContent = [
          headers.join(','),
          ...data.map(row => headers.map(header => {
            const value = row[header]
            // 处理包含逗号或引号的值
            if (typeof value === 'string' && (value.includes(',') || value.includes('"'))) {
              return `"${value.replace(/"/g, '""')}"`
            }
            return value
          }).join(','))
        ].join('\n')
        
        // 创建Blob并下载
        const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' })
        const link = document.createElement('a')
        const url = URL.createObjectURL(blob)
        
        link.setAttribute('href', url)
        link.setAttribute('download', `核销记录_${new Date().getTime()}.csv`)
        link.style.visibility = 'hidden'
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        URL.revokeObjectURL(url)
      } catch (error) {
        console.error('导出CSV失败:', error)
        this.$message.error('导出文件失败')
      }
    },
    
    // 格式化金额
    formatCurrency(value) {
      if (value === null || value === undefined) return '0.00'
      const num = Number(value)
      if (isNaN(num)) return '0.00'
      return num.toFixed(2)
    },
    
    // 格式化时间
    formatTime(timeStr) {
      if (!timeStr) return '-'
      return timeStr
    }
  }
}
</script>

<style scoped>
/* 自定义样式 */
.rounded-button {
  border-radius: 0.5rem;
}

/* 滚动条样式 */
.overflow-x-auto::-webkit-scrollbar {
  height: 6px;
}

.overflow-x-auto::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

.overflow-x-auto::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 3px;
}

.overflow-x-auto::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}

/* Element UI pagination 样式覆盖 */
:deep(.el-pagination) {
  padding: 0 !important;
}

:deep(.el-pagination .btn-prev),
:deep(.el-pagination .btn-next) {
  padding: 0 5px !important;
}

:deep(.el-pagination .el-pager li) {
  min-width: 28px !important;
  height: 28px !important;
  line-height: 28px !important;
}

/* 已核销行样式 */
tr.bg-green-50:hover {
  background-color: #f0fdf4 !important;
}
</style>


// WEBPACK FOOTER //
// src/views/statistics/hexiao/index.vue